/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, input, output, TemplateRef, contentChild, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { TableComponent, TableConfig, ActionEvent, SortChangeEvent } from 'nebula-ui-kit';

export interface CellTemplateContext<T> {
  $implicit: T;
  index: number;
  column?: any;
}

@Component({
  selector: 'app-data-table-wrapper',
  standalone: true,
  imports: [CommonModule, MatPaginatorModule, TableComponent],
  template: `
    <div class="data-table-wrapper">
      <lib-table
        [config]="tableConfigWithoutPagination()"
        [data]="data()"
        [loading]="loading()"
        [searchTerm]="searchTerm()"
        [sortColumn]="sortColumn()"
        [sortDirection]="sortDirection()"
        [pageSize]="pageSize()"
        [pageSizeOptions]="pageSizeOptions()"
        [selectable]="selectable()"
        [multiple]="multiple()"
        [selectedIds]="selectedIds()"
        (searchChange)="onSearchChange($event)"
        (sortChange)="onSortChange($event)"
        (action)="onAction($event)"
        (rowClick)="onRowClick($event)"
        (selectionChange)="onSelectionChange($event)"
      >
        <ng-template #cellTemplate let-item let-index="index" let-column="column">
          <ng-container
            [ngTemplateOutlet]="getCellTemplate()"
            [ngTemplateOutletContext]="{ $implicit: item, index: index, column: column }"
          >
          </ng-container>
        </ng-template>
      </lib-table>

      @if (showPagination() && totalElements() > 0) {
        <div class="pagination-container">
          <mat-paginator
            [length]="totalElements()"
            [pageSize]="pageSize()"
            [pageSizeOptions]="pageSizeOptions()"
            [pageIndex]="currentPage()"
            (page)="onExternalPageChange($event)"
            showFirstLastButtons
          >
          </mat-paginator>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .data-table-wrapper {
        width: 100%;
      }
      .pagination-container {
        margin-top: 16px;
        display: flex;
        justify-content: flex-end;
      }
    `,
  ],
})
export class DataTableWrapperComponent<T extends Record<string, any>> {
  tableConfig = input<TableConfig<T>>({ columns: [] });
  data = input<T[]>([]);
  loading = input<boolean>(false);
  searchTerm = input<string>('');
  sortColumn = input<string | null>(null);
  sortDirection = input<'asc' | 'desc' | null>(null);
  pageSize = input<number>(10);
  pageSizeOptions = input<number[]>([10, 20, 50]);

  showPagination = input<boolean>(true);
  totalElements = input<number>(0);
  currentPage = input<number>(0);

  selectable = input<boolean>(false);
  multiple = input<boolean>(true);
  selectedIds = input<any[]>([]);

  searchChange = output<string>();
  sortChange = output<SortChangeEvent<T>>();
  pageChange = output<PageEvent>();
  action = output<ActionEvent<T>>();
  rowClick = output<T>();
  selectionChange = output<T[]>();

  cellTemplate = contentChild<TemplateRef<CellTemplateContext<T>>>('cellTemplate');

  tableConfigWithoutPagination = computed(() => {
    const config = this.tableConfig();
    const multipleValue = this.multiple();

    return {
      ...config,
      showPagination: false,
      multiple: config.multiple !== undefined ? config.multiple : multipleValue,
    };
  });

  onSearchChange(term: string): void {
    this.searchChange.emit(term);
  }

  onSortChange(event: SortChangeEvent<T>): void {
    this.sortChange.emit(event);
  }

  onAction(event: ActionEvent<T>): void {
    this.action.emit(event);
  }

  onRowClick(item: T): void {
    this.rowClick.emit(item);
  }

  onExternalPageChange(event: PageEvent): void {
    this.pageChange.emit(event);
  }

  onSelectionChange(selectedItems: T[]): void {
    this.selectionChange.emit(selectedItems);
  }

  getCellTemplate(): TemplateRef<CellTemplateContext<T>> | null {
    return this.cellTemplate() || null;
  }
}
